import React from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { ScheduleOutlined } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { colors, radii, shadows, typography } from '../../styles/tokens';
import { CategoryArtwork } from './CategoryArtwork';

export interface ArticleCardProps {
  title: string;
  category: string;
  timestamp: string;
  readTimeMinutes: number;
  onClick?: () => void;
}

export const ArticleCard: React.FC<ArticleCardProps> = ({
  title,
  category,
  timestamp,
  readTimeMinutes,
  onClick,
}) => {
  const interactive = Boolean(onClick);

  return (
    <Card
      elevation={0}
      onClick={onClick}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: colors.paper,
        border: `1px solid ${colors.border}`,
        borderRadius: `${radii.card}px`,
        boxShadow: shadows.resting,
        cursor: interactive ? 'pointer' : 'default',
        transition: 'box-shadow 150ms ease, border-color 150ms ease',
        '&:hover': interactive
          ? { boxShadow: shadows.hover, borderColor: alpha(colors.primary, 0.35) }
          : undefined,
      }}
    >
      <Box sx={{ height: 160, bgcolor: colors.surface, overflow: 'hidden' }}>
        <CategoryArtwork category={category} />
      </Box>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Typography
          variant="overline"
          sx={{ color: colors.primary, fontWeight: 700, letterSpacing: '0.06em', mb: 0.5 }}
        >
          {category}
        </Typography>
        <Typography
          variant="subtitle1"
          component="h3"
          sx={{
            fontFamily: typography.fontFamilyDisplay,
            fontWeight: 600,
            color: colors.textPrimary,
            fontSize: '1.125rem',
            lineHeight: 1.35,
            mb: 1.5,
            flex: 1,
          }}
        >
          {title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: colors.textMuted }}>
          <Typography variant="caption" sx={{ color: colors.textSecondary }}>
            {timestamp}
          </Typography>
          <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: colors.textMuted }} />
          <ScheduleOutlined sx={{ fontSize: 14 }} />
          <Typography variant="caption" sx={{ color: colors.textSecondary }}>
            {readTimeMinutes} min read
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};
